// // PieChart.tsx
// import React, { useEffect, useState } from "react";
// import ApexCharts from "react-apexcharts";
// import { ApexOptions } from "apexcharts";
// import { useTheme } from "@mui/material/styles";
// import { useMutation } from "@tanstack/react-query";
// import { apiGetTurnoverReasonStats } from "../../api/dashboard";
// import useResponsive from "../../hooks/useResponsive";
// import { useNotificationService } from "../notification";
// import { useAuth } from "../../hooks/useAuth";
// import { dummyDataTurnoverRate, TurnoverRateProps } from "../charts/chart";

// const DashboardPieChart: React.FC = () => {
//   const isMedium = useResponsive("down", "md", "xl");
//   const isSmall = useResponsive("down", "sm", "xl");
//   const { showNotification } = useNotificationService();
//   const { token } = useAuth();

//   const [stats, setStats] = useState<TurnoverRateProps[]>(
//     [dummyDataTurnoverRate]
//   );

//   useEffect(() => {
//     mutation.mutate();
//   }, []);

//   const mutation = useMutation({
//     mutationFn: () => apiGetTurnoverReasonStats(token),
//     onSuccess: (data) => {
//       if (data?.status === "error") {
//         if ("msg" in data) {
//           showNotification(data?.msg ?? "", "error");
//           return;
//         }
//         return;
//       }
//       if ("data" in data) {
//         setStats(data?.data?.data);
//       }
//     },
//     onError: (error) => {
//       console.error("Fetch process failed", error);
//       showNotification("Fetch process failed", "error");
//     },
//   });
//   const series = stats?.map(stat => stat.count); // Your 4 values here

//   const options: ApexOptions = {
//     chart: {
//       type: "pie",
//       toolbar: {
//         show: false, // Disable the toolbar (menu)
//       },
//     },
//     labels: stats?.map(stat => stat.reason?.toUpperCase()), // Labels for each segment
//     title: {
//       text: "Reason",
//       align: "left",
//     },
//     colors: ["#D4DBEF", "#284CB1", "#6881C8", "#93A5D8"], // Optional: Custom colors
//     dataLabels: {
//       enabled: true, // Display data labels
//     },
//     legend: {
//       position: "bottom", // Position of the legend
//       markers: {
//         shape: "square",
//       },
//     },
//   };

//   return (
//     <ApexCharts options={options} series={series} type="pie" height={350} />
//   );
// };

// export default DashboardPieChart;

import React, { useEffect, useState } from "react";
import ApexCharts from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { apiGetTurnoverReasonStats } from "../../api/dashboard";
import useResponsive from "../../hooks/useResponsive";
import { useNotificationService } from "../notification";
import { useAuth } from "../../hooks/useAuth";
import { dummyDataTurnoverRate, TurnoverRateProps } from "../charts/chart";

const periods = ["monthly", "yearly"] as const;

// const DashboardPieChart: React.FC = () => {
//   const isMedium = useResponsive("down", "md", "xl");
//   const isSmall = useResponsive("down", "sm", "xl");

//   const [period, setPeriod] = useState<(typeof periods)[number]>("monthly");

//   const { showNotification } = useNotificationService();
//   const { token } = useAuth();

//   const { data, isLoading, isError } = useQuery<TurnoverRateProps[], Error>({
//     queryKey: ["turnover-reason"],
//     queryFn: () => apiGetTurnoverReasonStats(token, period),
//     initialData: [dummyDataTurnoverRate], // Treated as real data, won't refetch immediately
//   });

//   const stats = data?.length ? data : [dummyDataTurnoverRate];

//   const hasRealData = data && data.length > 0;

//   const series = hasRealData ? stats.map((stat) => stat.count) : [1]; // fallback 100%

//   const options: ApexOptions = {
//     chart: {
//       type: "pie",
//       toolbar: { show: false },
//     },
//     labels: hasRealData
//       ? stats.map((stat) => stat.reason?.toUpperCase())
//       : ["NO DATA"],
//     title: {
//       text: "Turnover Reason",
//       align: "left",
//     },
//     colors: ["#D4DBEF", "#2F66F4", "#6881C8", "#93A5D8", "#EF4444"], // more colors if needed
//     dataLabels: { enabled: true },
//     legend: {
//       position: "bottom",
//       markers: { shape: "square" },
//     },
//   };

//   return (
//     <>
//       {isLoading ? (
//         <p>Loading...</p>
//       ) : isError ? (
//         <p>Error fetching data.</p>
//       ) : (
//         <ApexCharts options={options} series={series} type="pie" height={350} />
//       )}
//     </>
//   );
// };

const DashboardPieChart: React.FC = () => {
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");

  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");

  const { showNotification } = useNotificationService();

  const { data, isLoading, isError } = useQuery<TurnoverRateProps[], Error>({
    queryKey: ["turnover-reason", period],
    queryFn: () => apiGetTurnoverReasonStats(period),
    initialData: [dummyDataTurnoverRate],
  });

  // 🚩 Validate & sanitize data
  const stats =
    Array.isArray(data) && data.length > 0
      ? data.filter(
          (stat) =>
            typeof stat?.count === "number" &&
            !isNaN(stat.count) &&
            stat?.reason,
        )
      : [];

  const hasRealData = stats.length > 0;

  const safeSeries = hasRealData ? stats.map((stat) => stat.count) : [1];
  const safeLabels = hasRealData
    ? stats.map((stat) => stat.reason!.toUpperCase())
    : ["NO DATA"];

  // };
  const options: ApexOptions = {
    chart: {
      type: "pie",
      toolbar: { show: false },
    },
    labels: safeLabels,
    title: {
      text: "Turnover Reason",
      align: "left",
    },
    colors: ["#D4DBEF", "#2F66F4", "#6881C8", "#93A5D8", "#EF4444"],
    dataLabels: { enabled: true },
    legend: {
      position: "bottom",
      markers: { shape: "square" },
    },
    states: {
      hover: {
        filter: {
          type: "none", // disables the default lightening
        },
      },
      active: {
        filter: {
          type: "none", // disables when clicked
        },
      },
    },
  };

  return (
    <>
      {isLoading ? (
        <p>Loading...</p>
      ) : isError ? (
        <p>Error fetching data.</p>
      ) : (
        <ApexCharts
          options={options}
          series={safeSeries}
          type="pie"
          height={350}
        />
      )}
    </>
  );
};

export default DashboardPieChart;
